import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

interface HeaderProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export default function Header({ mobileMenuOpen, setMobileMenuOpen }: HeaderProps) {
  const navItems = [
    { label: 'Features', href: '/#features' },
    { label: 'Security', href: '/#security' },
    { label: 'How it works', href: '/#how-it-works' },
    { label: 'FAQ', href: '/#faq' },
    { label: 'Support', href: '/#support' },
  ];

  const location = useLocation();

  // Scroll to section when the location hash changes
  useEffect(() => {
    // Only attempt to scroll if the location has a hash (e.g., #features, #faq)
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const scrollToSection = (href: string) => {
    // Ensure the href contains a valid section hash
    const targetHash = href.split('#')[1];
    if (!targetHash) return;  // If no valid hash, don't attempt to scroll

    // Check if the element exists on the current page
    const element = document.getElementById(targetHash);
    if (element) {
      // If the section exists, scroll to it
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If the section does not exist (cross-page navigation), navigate to the page
      window.location.href = href;
    }

    // Close the mobile menu
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
            <img src="/img/vlogo.svg" alt="Logo" className="logo" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer text-sm font-medium"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <button
              onClick={() => scrollToSection('#download')}
              className="bg-[#4C6FFF] hover:bg-[#3B5EEE] text-white px-6 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Download Vollet
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              {mobileMenuOpen ? (
                <i className="ri-close-line text-2xl text-gray-900"></i>
              ) : (
                <i className="ri-menu-line text-2xl text-gray-900"></i>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-white z-40">
          <nav className="flex flex-col p-6 gap-4 formxd">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="text-gray-900 text-lg font-medium py-3 text-left cursor-pointer hover:text-[#4C6FFF] transition-colors"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('#download')}
              className="bg-[#4C6FFF] hover:bg-[#3B5EEE] text-white px-6 py-3 rounded-full font-medium transition-colors cursor-pointer mt-4 whitespace-nowrap"
            >
              Download Vollet
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
